import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useAuth } from "../../../contexts/AuthContext";
import {
  updateOnboardingStep,
  completeOnboarding,
  upsertLawyerProfile,
  updateProfile,
  getOnboardingProgress,
  getLawyerProfile,
  getProfile,
  syncUserToBrevo,
} from "@/shared/db";
import { generateLawyerBioWithGroq } from "@/shared/ai";
import { toast } from "sonner";
import { ProgressBar } from "../components/ProgressBar";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { analytics } from "@/utils/analytics";
import {
  Button,
  Input,
  Textarea,
  SelectableGroup,
  CheckboxGroup,
} from "@/shared/ui";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { LawyerFormData } from "../types/onboarding.types";

export const LawyerOnboardingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const hasTrackedStarted = useRef(false);
  const [loading, setLoading] = useState(false);
  const [bioGenerating, setBioGenerating] = useState(false);
  const [showBioLanguagePicker, setShowBioLanguagePicker] = useState(false);

  const [formData, setFormData] = useState<LawyerFormData>({
    fullName: "",
    barNumber: "",
    university: "",
    yearsOfExperience: 0,
    practiceAreas: [],
    acceptCases: false,
    acceptClients: false,
    hourlyRate: "",
    phone: "",
    bio: "",
  });

  const steps = t("onboarding.lawyer.steps", {
    returnObjects: true,
  }) as string[];

  // Track onboarding started (once per session)
  useEffect(() => {
    if (user && !hasTrackedStarted.current) {
      hasTrackedStarted.current = true;
      analytics.onboardingStarted("lawyer");
    }
  }, [user]);

  // Load saved progress when component mounts
  useEffect(() => {
    const loadSavedProgress = async () => {
      if (!user) return;

      try {
        // Load onboarding progress
        const progress = await getOnboardingProgress(user.id);

        if (progress) {
          // Restore current step (remap from old 6-step flow if needed)
          if (progress.current_step && progress.current_step > 1) {
            if (progress.total_steps === 6) {
              // Remap old 6-step flow to new 3-step flow
              const old = progress.current_step;
              setCurrentStep(old <= 2 ? 1 : old <= 4 ? 2 : 3);
            } else {
              setCurrentStep(progress.current_step);
            }
          }

          // Try to load saved data from lawyer profile first
          try {
            const [savedLawyerProfile, baseProfile] = await Promise.all([
              getLawyerProfile(user.id),
              getProfile(user.id),
            ]);

            if (savedLawyerProfile) {
              setFormData({
                fullName: baseProfile?.full_name || "",
                barNumber: savedLawyerProfile.bar_number || "",
                university: savedLawyerProfile.university || "",
                yearsOfExperience: savedLawyerProfile.years_of_experience || 0,
                practiceAreas:
                  (savedLawyerProfile.practice_areas as string[]) || [],
                acceptCases: savedLawyerProfile.accept_cases || false,
                acceptClients: savedLawyerProfile.accept_clients || false,
                hourlyRate: savedLawyerProfile.hourly_rate?.toString() || "",
                phone: savedLawyerProfile.phone || "",
                bio: savedLawyerProfile.bio || "",
              });
              return; // Data loaded from profile, exit early
            }
          } catch {
            // Profile doesn't exist yet, continue to load from onboarding_data
          }

          // If no profile data, load from onboarding_data
          if (progress.onboarding_data) {
            const onboardingData = progress.onboarding_data as Record<
              string,
              unknown
            >;

            // Get the most recent step data (step with highest number)
            const stepKeys = Object.keys(onboardingData)
              .filter((key) => key.startsWith("step"))
              .sort((a, b) => {
                const numA = parseInt(a.replace("step", ""));
                const numB = parseInt(b.replace("step", ""));
                return numB - numA;
              });

            if (stepKeys.length > 0) {
              const latestStepData = onboardingData[
                stepKeys[0]
              ] as LawyerFormData;

              if (latestStepData) {
                setFormData((prev) => ({
                  ...prev,
                  ...latestStepData,
                }));
              }
            }
          }

          // Ensure fullName from base profile when no lawyer profile yet
          try {
            const baseProfile = await getProfile(user.id);
            if (baseProfile?.full_name) {
              setFormData((prev) => ({
                ...prev,
                fullName: baseProfile.full_name ?? prev.fullName,
              }));
            }
          } catch {
            // Ignore
          }
        }
      } catch (error) {
        console.error("Error loading saved progress:", error);
        // Don't show error to user, just continue with empty form
      }
    };

    loadSavedProgress();
  }, [user]);

  const handleNext = async () => {
    if (!user) return;

    // Validate step 1: only fullName is required
    if (currentStep === 1 && !formData.fullName?.trim()) {
      toast.error(t("onboarding.lawyer.step1.fullNameRequired"));
      return;
    }

    try {
      setLoading(true);

      // Save current step data to onboarding_progress
      await updateOnboardingStep(user.id, currentStep, {
        ...formData,
      });

      // Also save partial data to lawyer_profiles to prevent data loss
      // This ensures the user doesn't lose progress if they close the browser
      try {
        const partialProfile: {
          bar_number?: string | null;
          university?: string | null;
          years_of_experience?: number;
          practice_areas?: string[];
          accept_cases?: boolean;
          accept_clients?: boolean;
          hourly_rate?: number | null;
          phone?: string | null;
          bio?: string | null;
        } = {};

        // Only include fields that have been filled based on current step
        if (currentStep >= 1) {
          partialProfile.bar_number = formData.barNumber?.trim() || null;
          partialProfile.university =
            formData.university?.trim() || "To be updated";
          partialProfile.years_of_experience = formData.yearsOfExperience ?? 0;
          if (formData.phone) partialProfile.phone = formData.phone;
          if (formData.hourlyRate) {
            partialProfile.hourly_rate = parseFloat(formData.hourlyRate);
          }
        }

        if (currentStep >= 2) {
          if (formData.practiceAreas.length > 0) {
            partialProfile.practice_areas = formData.practiceAreas;
          }
          partialProfile.accept_cases = formData.acceptCases;
          partialProfile.accept_clients = formData.acceptClients;
        }

        if (currentStep >= 3 && formData.bio) {
          partialProfile.bio = formData.bio;
        }

        // Save full_name to profile when we have it (step 1)
        if (currentStep >= 1 && formData.fullName?.trim()) {
          await updateProfile(user.id, { full_name: formData.fullName.trim() });
        }

        // Update lawyer profile with partial data using upsert (creates if doesn't exist)
        if (Object.keys(partialProfile).length > 0) {
          await upsertLawyerProfile(
            user.id,
            partialProfile as Parameters<typeof upsertLawyerProfile>[1],
          );
        }
      } catch (profileError) {
        // Log error but don't block the flow
        console.warn("Could not save partial profile data:", profileError);
      }

      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        // Step 3 (Bio & Confirmation) — complete onboarding directly
        await completeOnboardingProcess();
      }
    } catch {
      toast.error(t("onboarding.lawyer.errors.save"));
    } finally {
      setLoading(false);
    }
  };

  const completeOnboardingProcess = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Update profile with full_name from lawyer onboarding
      if (formData.fullName?.trim()) {
        await updateProfile(user.id, { full_name: formData.fullName.trim() });
      }

      // Create lawyer profile (optional fields use placeholders for DB NOT NULL)
      await upsertLawyerProfile(user.id, {
        bar_number: formData.barNumber?.trim() || null,
        university: formData.university?.trim() || "To be updated",
        years_of_experience: formData.yearsOfExperience ?? 0,
        practice_areas: formData.practiceAreas?.length
          ? formData.practiceAreas
          : [],
        accept_cases: formData.acceptCases,
        accept_clients: formData.acceptClients,
        hourly_rate: formData.hourlyRate
          ? parseFloat(formData.hourlyRate)
          : null,
        phone: formData.phone || null,
        bio: formData.bio || null,
      });

      // Complete onboarding
      await completeOnboarding(user.id);
      analytics.onboardingCompleted("lawyer");
      await syncUserToBrevo(user.id);

      // Wait for profile to refresh and verify it's updated
      let attempts = 0;
      const maxAttempts = 5;

      while (attempts < maxAttempts) {
        await refreshProfile();
        const p = await getProfile(user.id);
        if (p?.onboarding_completed === true) {
          break;
        }
        // Wait a bit before retrying
        await new Promise((resolve) => setTimeout(resolve, 300));
        attempts++;
      }

      toast.success(t("onboarding.lawyer.success"));

      // Always send lawyers to /dashboard/lawyer (never to /dashboard documents)
      // refreshProfile was already called in the polling loop above; no extra delay needed
      sessionStorage.removeItem("redirectAfterOnboarding");
      navigate("/dashboard/lawyer");
    } catch {
      toast.error(t("onboarding.lawyer.errors.complete"));
    } finally {
      setLoading(false);
    }
  };

  const generateBioWithLanguage = async (language: "es" | "en") => {
    setBioGenerating(true);
    try {
      const practiceAreaLabels = formData.practiceAreas.map(
        (code) => t(`onboarding.lawyer.step3.areas.${code}`) || code,
      );
      const bio = await generateLawyerBioWithGroq({
        barNumber: formData.barNumber,
        university: formData.university,
        yearsOfExperience: formData.yearsOfExperience,
        practiceAreas: practiceAreaLabels,
        hourlyRate: formData.hourlyRate || undefined,
        acceptCases: formData.acceptCases,
        acceptClients: formData.acceptClients,
        currentBio: formData.bio || undefined,
        language,
      });
      setFormData((prev) => ({ ...prev, bio }));
      toast.success(t("onboarding.lawyer.step5.aiGeneratedSuccess"));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast.error(
        msg.includes("initialized") || msg.includes("not configured")
          ? t("onboarding.lawyer.step5.aiNotAvailable")
          : t("onboarding.lawyer.step5.aiError"),
      );
    } finally {
      setBioGenerating(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      // Step 1: Professional Info (old steps 1+2)
      case 1:
        return (
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-tiber-900 tracking-tight">
              {t("onboarding.lawyer.step1.title")}
            </h2>
            <div>
              <Input
                label={t("onboarding.lawyer.step1.fullName")}
                placeholder={t("onboarding.lawyer.step1.fullNamePlaceholder")}
                value={formData.fullName}
                onChange={(e) =>
                  setFormData({ ...formData, fullName: e.target.value })
                }
              />
            </div>
            <div>
              <Input
                label={t("onboarding.lawyer.step1.barNumberOptional")}
                placeholder={t("onboarding.lawyer.step1.barNumberPlaceholder")}
                value={formData.barNumber}
                onChange={(e) =>
                  setFormData({ ...formData, barNumber: e.target.value })
                }
              />
            </div>
            <div>
              <Input
                label={t("onboarding.lawyer.step1.universityOptional")}
                placeholder={t("onboarding.lawyer.step1.universityPlaceholder")}
                value={formData.university}
                onChange={(e) =>
                  setFormData({ ...formData, university: e.target.value })
                }
              />
            </div>
            <div>
              <Input
                label={t("onboarding.lawyer.step1.yearsExperienceOptional")}
                placeholder={t(
                  "onboarding.lawyer.step1.yearsExperiencePlaceholder",
                )}
                value={formData.yearsOfExperience}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    yearsOfExperience: parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-tiber-700 mb-1">
                {t("onboarding.lawyer.step2.phone")}
              </label>
              <PhoneInput
                international
                countryCallingCodeEditable={false}
                defaultCountry="DO"
                value={formData.phone}
                onChange={(value) =>
                  setFormData({ ...formData, phone: value || "" })
                }
                className="phone-input-wrapper"
              />
              <style>{`
                .phone-input-wrapper {
                  display: flex;
                  align-items: center;
                  border: 1px solid #d1d5db;
                  border-radius: 0.5rem;
                  padding: 0.625rem 0.75rem;
                  transition: all 0.2s;
                }
                .phone-input-wrapper:focus-within {
                  border-color: #0d6d6e;
                  box-shadow: 0 0 0 2px rgba(13, 109, 110, 0.2);
                }
                .phone-input-wrapper .PhoneInputCountry {
                  margin-right: 0.5rem;
                }
                .phone-input-wrapper .PhoneInputInput {
                  border: none;
                  outline: none;
                  flex: 1;
                  font-size: 0.875rem;
                }
              `}</style>
            </div>
            <div>
              <Input
                label={t("onboarding.lawyer.step2.hourlyRate")}
                placeholder={t("onboarding.lawyer.step2.hourlyRatePlaceholder")}
                value={formData.hourlyRate}
                onChange={(e) =>
                  setFormData({ ...formData, hourlyRate: e.target.value })
                }
              />
            </div>
          </motion.div>
        );

      // Step 2: Practice & Preferences (old steps 3+4)
      case 2: {
        const areas = [
          { code: "civil", label: t("onboarding.lawyer.step3.areas.civil") },
          { code: "penal", label: t("onboarding.lawyer.step3.areas.penal") },
          {
            code: "laboral",
            label: t("onboarding.lawyer.step3.areas.laboral"),
          },
          {
            code: "comercial",
            label: t("onboarding.lawyer.step3.areas.comercial"),
          },
          { code: "fiscal", label: t("onboarding.lawyer.step3.areas.fiscal") },
          {
            code: "administrativo",
            label: t("onboarding.lawyer.step3.areas.administrativo"),
          },
        ];

        return (
          <div className="space-y-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-tiber-900 tracking-tight">
              {t("onboarding.lawyer.step3.titleOptional")}
            </h2>
            <p className="text-tiber-800">
              {t("onboarding.lawyer.step3.descriptionOptional")}
            </p>
            <SelectableGroup
              options={areas.map((area) => ({
                value: area.code,
                label: area.label,
              }))}
              selectedValues={formData.practiceAreas}
              onChange={(selectedValues) => {
                setFormData((prev) => ({
                  ...prev,
                  practiceAreas: selectedValues,
                }));
              }}
              multiple={true}
              columns={2}
            />

            <div className="pt-4 border-t border-tiber-100">
              <h3 className="text-lg font-bold text-tiber-900 mb-4">
                {t("onboarding.lawyer.step4.title")}
              </h3>
              <div className="space-y-4">
                <CheckboxGroup
                  name="acceptCases"
                  options={[
                    {
                      value: "acceptCases",
                      label: t("onboarding.lawyer.step4.acceptCases"),
                      description: t("onboarding.lawyer.step4.acceptCasesDesc"),
                    },
                  ]}
                  selectedValues={formData.acceptCases ? ["acceptCases"] : []}
                  onChange={(selectedValues) => {
                    setFormData({
                      ...formData,
                      acceptCases: selectedValues.includes("acceptCases"),
                    });
                  }}
                />

                <CheckboxGroup
                  name="acceptClients"
                  options={[
                    {
                      value: "acceptClients",
                      label: t("onboarding.lawyer.step4.acceptClients"),
                      description: t(
                        "onboarding.lawyer.step4.acceptClientsDesc",
                      ),
                    },
                  ]}
                  selectedValues={
                    formData.acceptClients ? ["acceptClients"] : []
                  }
                  onChange={(selectedValues) => {
                    setFormData({
                      ...formData,
                      acceptClients: selectedValues.includes("acceptClients"),
                    });
                  }}
                />
              </div>
            </div>
          </div>
        );
      }

      // Step 3: Bio & Confirmation (old steps 5+6)
      case 3:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-tiber-900 tracking-tight">
              {t("onboarding.lawyer.step5.title")}
            </h2>
            <p className="text-sm text-tiber-600 leading-relaxed">
              {t("onboarding.lawyer.step5.bioHint")}
            </p>
            <div>
              <Textarea
                label={t("onboarding.lawyer.step5.bio")}
                placeholder={t("onboarding.lawyer.step5.bioPlaceholder")}
                value={formData.bio}
                onChange={(e) =>
                  setFormData({ ...formData, bio: e.target.value })
                }
                rows={5}
              />
              <div className="mt-2 flex flex-col items-end gap-2">
                {showBioLanguagePicker ? (
                  <div className="flex flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto">
                    <p className="text-sm text-tiber-700">
                      {t("onboarding.lawyer.step5.aiLanguageQuestion")}
                    </p>
                    <div className="flex gap-2 flex-wrap justify-end">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={bioGenerating}
                        onClick={() => {
                          setShowBioLanguagePicker(false);
                          generateBioWithLanguage("es");
                        }}
                        className="cursor-pointer"
                      >
                        {t("onboarding.lawyer.step5.aiLanguageSpanish")}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={bioGenerating}
                        onClick={() => {
                          setShowBioLanguagePicker(false);
                          generateBioWithLanguage("en");
                        }}
                        className="cursor-pointer"
                      >
                        {t("onboarding.lawyer.step5.aiLanguageEnglish")}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={bioGenerating}
                        onClick={() => setShowBioLanguagePicker(false)}
                        className="cursor-pointer text-tiber-600"
                      >
                        {t("common.cancel")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={bioGenerating}
                    onClick={() => setShowBioLanguagePicker(true)}
                    leftIcon={
                      bioGenerating ? (
                        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <SparklesIcon className="w-4 h-4" />
                      )
                    }
                    className="cursor-pointer"
                  >
                    {bioGenerating
                      ? t("onboarding.lawyer.step5.aiGenerating")
                      : t("onboarding.lawyer.step5.aiGenerateButton")}
                  </Button>
                )}
              </div>
            </div>

            {/* Review Summary */}
            <div className="bg-linear-to-br from-tiber-50 to-atlantis-50/50 p-6 sm:p-8 rounded-xl border border-tiber-100">
              <h3 className="font-semibold text-tiber-800 mb-5 text-base">
                {t("onboarding.lawyer.step6.review")}
              </h3>
              <ul className="space-y-3 text-sm font-medium text-tiber-800">
                <li>
                  <strong>{t("onboarding.lawyer.step6.barNumber")}:</strong>{" "}
                  {formData.barNumber?.trim() ||
                    t("onboarding.lawyer.step6.notSpecified")}
                </li>
                <li>
                  <strong>{t("onboarding.lawyer.step6.university")}:</strong>{" "}
                  {formData.university?.trim() ||
                    t("onboarding.lawyer.step6.notSpecified")}
                </li>
                <li>
                  <strong>{t("onboarding.lawyer.step6.experience")}:</strong>{" "}
                  {formData.yearsOfExperience > 0
                    ? `${formData.yearsOfExperience} ${t("onboarding.lawyer.step6.years")}`
                    : t("onboarding.lawyer.step6.notSpecified")}
                </li>
                <li>
                  <strong>{t("onboarding.lawyer.step6.areas")}:</strong>{" "}
                  {formData.practiceAreas?.length
                    ? `${formData.practiceAreas.length} ${t("onboarding.lawyer.step6.selected")}`
                    : t("onboarding.lawyer.step6.notSpecified")}
                </li>
                {formData.hourlyRate &&
                parseFloat(formData.hourlyRate) > 0 ? (
                  <li>
                    <strong>{t("onboarding.lawyer.step6.rate")}:</strong> $
                    {formData.hourlyRate}
                    {t("onboarding.lawyer.step6.perHour")}
                  </li>
                ) : (
                  <li>
                    <strong>{t("onboarding.lawyer.step6.rate")}:</strong>{" "}
                    {t("onboarding.lawyer.step6.notSpecified")}
                  </li>
                )}
              </ul>
            </div>
            <p className="text-tiber-700 text-sm leading-relaxed">
              {t("onboarding.lawyer.step6.nextStep")}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        if (!formData.fullName?.trim()) return false;
        if (formData.hourlyRate.trim() !== "") {
          const hourlyRateValue = parseFloat(formData.hourlyRate);
          if (isNaN(hourlyRateValue) || hourlyRateValue <= 0) {
            return false;
          }
        }
        return true;
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background: gradient mesh + subtle pattern */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(165deg, #f1fcfb 0%, #fcfaf8 40%, #f0fdf4 100%)",
        }}
      />
      <div
        className="absolute inset-0 -z-10 opacity-[0.015]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23164b4a' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="py-10 px-4 sm:py-14">
        <div className="max-w-2xl mx-auto">
          {/* Logo */}
          <Link
            to="/"
            className="inline-block mb-8 opacity-90 hover:opacity-100 transition-opacity duration-200"
            aria-label="Lawgentia Home"
          >
            <img
              src="/images/brand/lawgentia-logo.png"
              alt="Lawgentia"
              className="h-10 w-auto"
            />
          </Link>

          {/* Progress */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <ProgressBar
              currentStep={currentStep}
              totalSteps={3}
              steps={steps}
            />
          </motion.div>

          {/* Form Card - elevated with refined shadow */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white rounded-2xl shadow-[0_4px_24px_-4px_rgba(20,141,138,0.08),0_8px_32px_-8px_rgba(20,141,138,0.12)] border border-tiber-100/60 p-8 sm:p-10"
          >
            {renderStep()}

            {/* Navigation */}
            <div className="flex gap-4 mt-10 pt-8 border-t border-tiber-100">
              <Button
                variant="secondary"
                onClick={handleBack}
                disabled={currentStep === 1 || loading}
                size="md"
                fullWidth
                isLoading={loading}
                leftIcon={<ArrowLeftIcon className="w-4 h-4" />}
                className="min-h-[44px] cursor-pointer transition-colors duration-200"
              >
                {t("onboarding.lawyer.buttons.back")}
              </Button>

              <Button
                variant="primary"
                onClick={handleNext}
                disabled={!isStepValid() || loading}
                size="md"
                fullWidth
                isLoading={loading}
                rightIcon={<ArrowRightIcon className="w-4 h-4" />}
                className="min-h-[44px] cursor-pointer transition-colors duration-200"
              >
                {loading
                  ? t("onboarding.lawyer.buttons.saving")
                  : currentStep === 3
                    ? t("onboarding.lawyer.buttons.complete")
                    : t("onboarding.lawyer.buttons.next")}
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
